import React, { useState, useEffect } from 'react';
import { 
  FaGoogle 
} from 'react-icons/fa';
import { 
  Building,
  Users,
  Download,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Shield,
  UserX,
  Loader2
} from 'lucide-react';
import { db, doc, getDoc, setDoc, writeBatch } from '../../firebase';
import styles from '../CSS/WorkspaceSync.module.css';

const WorkspaceSync = ({ 
  users, 
  currentUser, 
  onUpdate, 
  showFeedback,
  isVisible,
  onToggle,
  isModal = false,
  roles = []
}) => {
  // URL del servidor Render
  const SERVER_URL = 'https://truebavirtual.onrender.com';
  
  // Estados
  const [orgUnits, setOrgUnits] = useState([]);
  const [workspaceUsers, setWorkspaceUsers] = useState([]);
  const [syncMappings, setSyncMappings] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [selectedOrgUnit, setSelectedOrgUnit] = useState('');
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [isConfigured, setIsConfigured] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  // Verificar estado del servidor y cargar datos instantáneamente
  const checkServerAndLoadData = async () => {
    try {
      console.log('🔍 Conectando al servidor...');
      setIsLoading(true);
      
      // Health check - RUTA ACTUALIZADA
      const healthResponse = await fetch(`${SERVER_URL}/api/workspace/health`);
      if (!healthResponse.ok) throw new Error('Servidor no disponible');
      
      const health = await healthResponse.json();
      setIsConfigured(health.authenticated);
      
      if (!health.authenticated) {
        showFeedback("error", "El servidor no está autenticado con Google Workspace");
        setIsInitialLoad(false);
        return;
      }

      // Cargar datos instantáneamente - RUTA ACTUALIZADA
      console.log('📥 Cargando datos desde servidor...');
      const dataResponse = await fetch(`${SERVER_URL}/api/workspace/data`);
      if (!dataResponse.ok) throw new Error('Error obteniendo datos');
      
      const data = await dataResponse.json();

      // Procesar unidades organizativas
      const units = data.organizationUnits || [];
      const filteredUnits = units
        .filter(unit => unit.orgUnitPath !== '/')
        .map(unit => ({
          ...unit,
          userCount: 0
        }));

      setOrgUnits(filteredUnits);

      // Procesar usuarios
      const workspaceUsersData = data.users || [];
      const processedUsers = workspaceUsersData.map(user => {
        const localUser = users.find(lu => lu.id === user.primaryEmail);
        return {
          ...user,
          isExisting: !!localUser,
          isWorkspaceSync: localUser?.syncedFromWorkspace || false,
          localRole: localUser?.role || mapOrgUnitToRole(user.orgUnitPath),
          orgUnitName: getOrgUnitName(user.orgUnitPath, filteredUnits)
        };
      });

      // Actualizar conteo de usuarios por OU
      const unitsWithCount = filteredUnits.map(ou => ({
        ...ou,
        userCount: processedUsers.filter(user => user.orgUnitPath === ou.orgUnitPath).length
      }));

      setOrgUnits(unitsWithCount);
      setWorkspaceUsers(processedUsers);
      setInitialLoadDone(true);
      setIsInitialLoad(false);
      
      console.log('✅ Datos cargados instantáneamente');

    } catch (error) {
      console.error('❌ Error:', error);
      setIsConfigured(false);
      setIsInitialLoad(false);
      showFeedback("error", "Error conectando con el servidor: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Actualizar datos (forzar refresh en el servidor)
  const refreshWorkspaceData = async () => {
    if (!isConfigured) return;

    try {
      setIsLoading(true);
      console.log('🔄 Actualizando datos...');

      // Forzar actualización en el servidor - RUTA ACTUALIZADA
      await fetch(`${SERVER_URL}/api/workspace/refresh`, { method: 'POST' });
      
      // Recargar datos actualizados
      await checkServerAndLoadData();
      
    } catch (error) {
      console.error('❌ Error actualizando:', error);
      showFeedback("error", "Error al actualizar datos: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Cargar historial de sincronización
  const loadSyncHistory = async () => {
    try {
      const docRef = doc(db, 'settings', 'workspace_sync');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        setLastSync(data.lastSync);
        setSyncMappings(data.syncMappings || []);
      }
    } catch (error) {
      console.error('Error loading sync history:', error);
    }
  };

  // Función helper para crear datos de usuario limpios
  const createCleanUserData = (workspaceUser, isNew = false) => {
    const workspaceData = {};
    if (workspaceUser.primaryEmail) workspaceData.primaryEmail = workspaceUser.primaryEmail;
    if (workspaceUser.recoveryEmail) workspaceData.recoveryEmail = workspaceUser.recoveryEmail;
    if (workspaceUser.lastLoginTime) workspaceData.lastLoginTime = workspaceUser.lastLoginTime;
    if (workspaceUser.thumbnailPhotoUrl) workspaceData.thumbnailPhotoUrl = workspaceUser.thumbnailPhotoUrl;
    workspaceData.isAdmin = workspaceUser.isAdmin || false;
    workspaceData.isDelegatedAdmin = workspaceUser.isDelegatedAdmin || false;

    const userData = {
      firstName: workspaceUser.name?.givenName || '',
      lastName: workspaceUser.name?.familyName || '',
      role: mapOrgUnitToRole(workspaceUser.orgUnitPath),
      orgUnit: workspaceUser.orgUnitPath || '',
      orgUnitName: getOrgUnitName(workspaceUser.orgUnitPath),
      syncedFromWorkspace: true,
      workspaceId: workspaceUser.id || '',
      workspaceCreated: workspaceUser.creationTime || new Date().toISOString(),
      suspended: workspaceUser.suspended || false,
      lastSyncDate: new Date().toISOString(),
      workspaceData
    };

    if (isNew) {
      userData.createdAt = new Date().toISOString();
    } else {
      userData.updatedAt = new Date().toISOString();
    }

    return userData;
  };

  // Funciones auxiliares
  const getOrgUnitName = (orgUnitPath, units = orgUnits) => {
    const orgUnit = units.find(ou => ou.orgUnitPath === orgUnitPath);
    return orgUnit ? orgUnit.name : orgUnitPath.split('/').pop();
  };

  const mapOrgUnitToRole = (orgUnitPath) => {
    const defaultMappings = {
      '/IT': 'admin',
      '/Sistemas': 'admin',
      '/Administracion': 'admin',
      '/RRHH': 'admin',
      '/Recursos Humanos': 'admin',
      '/Direccion': 'admin',
      '/Gerencia': 'admin'
    };
    
    const customMapping = syncMappings.find(m => m.orgUnitPath === orgUnitPath);
    if (customMapping) return customMapping.role;
    
    return defaultMappings[orgUnitPath] || 'user';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Sincronizar un usuario individual
  const syncSingleUser = async (workspaceUser) => {
    try {
      const batch = writeBatch(db);
      const userRef = doc(db, 'users', workspaceUser.primaryEmail);
      const userDoc = await getDoc(userRef);
      
      const userData = createCleanUserData(workspaceUser, !userDoc.exists());

      if (userDoc.exists()) {
        batch.update(userRef, userData);
      } else {
        batch.set(userRef, userData);
      }

      await batch.commit();

      // Guardar configuración de sincronización
      const syncConfigRef = doc(db, 'settings', 'workspace_sync');
      await setDoc(syncConfigRef, {
        syncMappings,
        lastSync: new Date().toISOString(),
        syncedUsersCount: userDoc.exists() ? 0 : 1,
        updatedUsersCount: userDoc.exists() ? 1 : 0,
        totalUsersInWorkspace: workspaceUsers.length,
        updatedBy: currentUser?.email || 'admin',
        serverUrl: SERVER_URL
      }, { merge: true });

      setLastSync(new Date().toISOString());
      
      showFeedback("success", `Usuario ${workspaceUser.name?.givenName || workspaceUser.primaryEmail} sincronizado correctamente`);
      onUpdate();

    } catch (error) {
      console.error('Error syncing single user:', error);
      showFeedback("error", "Error al sincronizar usuario: " + error.message);
    }
  };

  const toggleUserSelection = (email) => {
    setSelectedUsers(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(email)) {
        newSelected.delete(email);
      } else {
        newSelected.add(email);
      }
      return newSelected;
    });
  };

  // Sincronizar usuarios seleccionados
  const syncSelectedUsers = async () => {
    if (selectedUsers.size === 0) {
      showFeedback("warning", "Seleccione usuarios para sincronizar");
      return;
    }

    try {
      const batch = writeBatch(db);
      let syncedCount = 0;
      let updatedCount = 0;

      for (const email of selectedUsers) {
        const workspaceUser = workspaceUsers.find(u => u.primaryEmail === email);
        if (!workspaceUser) continue;

        const userRef = doc(db, 'users', email);
        const userDoc = await getDoc(userRef);
        
        const userData = createCleanUserData(workspaceUser, !userDoc.exists());

        if (userDoc.exists()) {
          batch.update(userRef, userData);
          updatedCount++;
        } else {
          batch.set(userRef, userData);
          syncedCount++;
        }
      }

      await batch.commit();

      // Guardar configuración de sincronización
      const syncConfigRef = doc(db, 'settings', 'workspace_sync');
      await setDoc(syncConfigRef, {
        syncMappings,
        lastSync: new Date().toISOString(),
        syncedUsersCount: syncedCount,
        updatedUsersCount: updatedCount,
        totalUsersInWorkspace: workspaceUsers.length,
        updatedBy: currentUser?.email || 'admin',
        serverUrl: SERVER_URL
      }, { merge: true });

      setLastSync(new Date().toISOString());
      setSelectedUsers(new Set());
      
      showFeedback("success", `Sincronización completada: ${syncedCount} nuevos, ${updatedCount} actualizados`);
      onUpdate();

    } catch (error) {
      console.error('Error syncing users:', error);
      showFeedback("error", "Error al sincronizar usuarios: " + error.message);
    }
  };

  // Sincronizar usuarios de una OU específica
  const syncOrgUnitUsers = async (orgUnitPath) => {
    const usersToSync = workspaceUsers.filter(wu => 
      wu.orgUnitPath === orgUnitPath && 
      !users.find(u => u.id === wu.primaryEmail)
    );

    if (usersToSync.length === 0) {
      showFeedback("warning", "No hay usuarios nuevos para sincronizar en esta unidad");
      return;
    }

    try {
      const batch = writeBatch(db);
      let syncedCount = 0;

      for (const workspaceUser of usersToSync) {
        const userRef = doc(db, 'users', workspaceUser.primaryEmail);
        const userData = createCleanUserData(workspaceUser, true);
        batch.set(userRef, userData);
        syncedCount++;
      }

      await batch.commit();

      // Guardar configuración de sincronización
      const syncConfigRef = doc(db, 'settings', 'workspace_sync');
      await setDoc(syncConfigRef, {
        syncMappings,
        lastSync: new Date().toISOString(),
        syncedUsersCount: syncedCount,
        totalUsersInWorkspace: workspaceUsers.length,
        updatedBy: currentUser?.email || 'admin',
        serverUrl: SERVER_URL
      }, { merge: true });

      setLastSync(new Date().toISOString());
      
      showFeedback("success", `${syncedCount} usuarios sincronizados desde ${getOrgUnitName(orgUnitPath)}`);
      onUpdate();

    } catch (error) {
      console.error('Error syncing OU users:', error);
      showFeedback("error", "Error al sincronizar usuarios: " + error.message);
    }
  };

  // Effects
  useEffect(() => {
    const initialize = async () => {
      await loadSyncHistory();
      
      if (isVisible && !initialLoadDone) {
        await checkServerAndLoadData();
      }
    };
    initialize();
  }, [isVisible]);

  if (!isVisible) return null;

  // Pantalla de carga inicial elegante
  if (isInitialLoad && isLoading) {
    return (
      <div className={styles.workspacePanel} data-modal={isModal}>
        <div className={styles.initialLoadingContainer}>
          <Loader2 size={48} className={styles.loadingSpinner} />
          <p>Conectando con Google Workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.workspacePanel} data-modal={isModal}>
      <div className={styles.workspacePanelHeader}>
        <h3>
          <FaGoogle className={styles.googleIcon} />
          Sincronización con Google Workspace
        </h3>
        <div className={styles.headerActions}>
          {lastSync && (
            <div className={styles.lastSync}>
              Última sincronización: {formatDate(lastSync)}
            </div>
          )}
          <button
            className={styles.closeButton}
            onClick={onToggle}
            title="Cerrar panel de Workspace"
          >
            ×
          </button>
        </div>
      </div>

      {/* Mensaje de servidor no disponible */}
      {!isConfigured && !isInitialLoad && (
        <div className={styles.serverError}>
          <div className={styles.serverErrorContent}>
            <AlertCircle size={48} className={styles.serverErrorIcon} />
            <h4>Servidor no disponible</h4>
            
            <button
              className={styles.retryButton}
              onClick={checkServerAndLoadData}
              disabled={isLoading}
            >
              <RefreshCw size={16} className={isLoading ? styles.spinning : ''} />
              Reintentar conexión
            </button>
          </div>
        </div>
      )}

      {/* Panel principal (solo si está configurado y no está cargando) */}
      {isConfigured && !isInitialLoad && (
        <>
          {/* Acción para actualizar datos */}
          {/* Acción para actualizar datos */}
            <div className={styles.workspaceActions}>
              <button
                className={styles.refreshButton}
                onClick={refreshWorkspaceData}
                disabled={isLoading}
              >
                <RefreshCw size={16} className={isLoading ? styles.spinning : ''} />
                Actualizar Datos
              </button>
              
              {workspaceUsers.length > 0 && (
                <div className={styles.workspaceStats}>
                  <span className={styles.statBadge}>
                    <Users size={14} />
                    <span className={styles.statNumber}>{workspaceUsers.length}</span>
                    <span className={styles.statText}> usuarios</span>
                  </span>
                  <span className={styles.statBadge}>
                    <Building size={14} />
                    <span className={styles.statNumber}>
                      {/* AQUÍ está el cambio 👇 */}
                      {orgUnits.filter(ou => {
                        const usersInOU = workspaceUsers.filter(wu => wu.orgUnitPath === ou.orgUnitPath);
                        const unsyncedUsers = usersInOU.filter(wu => !users.find(u => u.id === wu.primaryEmail));
                        return unsyncedUsers.length > 0;
                      }).length}
                    </span>
                    <span className={styles.statText}> UO's</span>
                  </span>
                </div>
              )}
            </div>

          {/* Selector de Unidad Organizativa */}
          {orgUnits.length > 0 && (
            <div className={styles.ouSelectorSection}>
              <h4 className={styles.sectionTitle}>
                <Building size={18} />
                Seleccionar Unidad Organizativa para Sincronizar
              </h4>
              
              <div className={styles.ouSelector}>
                <div className={styles.ouSelectContainer}>
                  <select
                    value={selectedOrgUnit}
                    onChange={(e) => setSelectedOrgUnit(e.target.value)}
                    className={styles.ouSelect}
                  >
                    <option value="">Seleccionar unidad organizativa...</option>
                    {orgUnits
                      .filter(orgUnit => {
                        const usersInOU = workspaceUsers.filter(wu => wu.orgUnitPath === orgUnit.orgUnitPath);
                        return usersInOU.length > 0;
                      })
                      .map(orgUnit => {
                        const usersInOU = workspaceUsers.filter(wu => wu.orgUnitPath === orgUnit.orgUnitPath);
                        const unsyncedUsers = usersInOU.filter(wu => !users.find(u => u.id === wu.primaryEmail));
                        
                        return (
                          <option key={orgUnit.orgUnitPath} value={orgUnit.orgUnitPath}>
                            {orgUnit.name} ({usersInOU.length} usuarios, {unsyncedUsers.length} disponibles)
                          </option>
                        );
                      })}
                  </select>
                  
                  {selectedOrgUnit && (
                    <button
                      className={styles.clearOUButton}
                      onClick={() => setSelectedOrgUnit('')}
                      title="Limpiar selección"
                    >
                      ×
                    </button>
                  )}
                </div>

                {selectedOrgUnit && (
                  <div className={styles.ouSummary}>
                    {(() => {
                      const selectedOU = orgUnits.find(ou => ou.orgUnitPath === selectedOrgUnit);
                      const usersInOU = workspaceUsers.filter(wu => wu.orgUnitPath === selectedOrgUnit);
                      const unsyncedUsers = usersInOU.filter(wu => !users.find(u => u.id === wu.primaryEmail));
                      const syncedUsers = usersInOU.filter(wu => users.find(u => u.id === wu.primaryEmail));
                      
                      return (
                        <>
                          <div className={styles.ouSummaryInfo}>
                            <h5>{selectedOU?.name}</h5>
                            <span className={styles.ouPath}>{selectedOrgUnit}</span>
                            <div className={styles.ouStats}>
                              <span className={styles.statItem}>
                                <Users size={14} />
                                {usersInOU.length} total
                              </span>
                              <span className={styles.statItem}>
                                <CheckCircle size={14} />
                                {syncedUsers.length} sincronizados
                              </span>
                              <span className={styles.statItem}>
                                <Download size={14} />
                                {unsyncedUsers.length} disponibles
                              </span>
                            </div>
                          </div>
                          
                          <div className={styles.roleConfiguration}>
                            <label className={styles.roleLabel}>
                              Rol para nuevos usuarios:
                            </label>
                            <select
                              value={syncMappings.find(m => m.orgUnitPath === selectedOrgUnit)?.role || mapOrgUnitToRole(selectedOrgUnit)}
                              onChange={(e) => {
                                const newMappings = syncMappings.filter(m => m.orgUnitPath !== selectedOrgUnit);
                                newMappings.push({
                                  orgUnitPath: selectedOrgUnit,
                                  role: e.target.value,
                                  orgUnitName: selectedOU?.name || selectedOrgUnit
                                });
                                setSyncMappings(newMappings);
                              }}
                              className={styles.roleConfigSelect}
                            >
                              <option value="user">Usuario</option>
                              <option value="admin">Administrador</option>
                              {roles.map(role => (
                                <option key={role.id} value={role.id}>
                                  {role.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Lista de usuarios de la OU seleccionada */}
          {selectedOrgUnit && workspaceUsers.length > 0 && (() => {
            const usersInOU = workspaceUsers.filter(wu => wu.orgUnitPath === selectedOrgUnit);
            
            if (usersInOU.length === 0) {
              return (
                <div className={styles.noUsersMessage}>
                  <div className={styles.noUsersContent}>
                    <Users size={48} className={styles.noUsersIcon} />
                    <h4>No hay usuarios en esta unidad organizativa</h4>
                    <p>La unidad organizativa seleccionada no contiene ningún usuario.</p>
                  </div>
                </div>
              );
            }

            const unsyncedUsers = usersInOU.filter(wu => !users.find(u => u.id === wu.primaryEmail));
            
            return (
              <div className={styles.ouUsersSection}>
                <div className={styles.ouUsersHeader}>
                  <h4>
                    <Users size={18} />
                    Usuarios en {orgUnits.find(ou => ou.orgUnitPath === selectedOrgUnit)?.name}
                  </h4>
                  
                  {unsyncedUsers.length > 0 && (
                    <button
                      className={styles.syncAllOUButton}
                      onClick={() => syncOrgUnitUsers(selectedOrgUnit)}
                      disabled={isLoading}
                    >
                      <Download size={16} />
                      Sincronizar todos ({unsyncedUsers.length})
                    </button>
                  )}
                </div>

                <div className={styles.ouUsersList}>
                  {usersInOU.map(wsUser => {
                    const isAlreadySynced = users.find(u => u.id === wsUser.primaryEmail);
                    const isSelected = selectedUsers.has(wsUser.primaryEmail);
                    
                    return (
                      <div 
                        key={wsUser.primaryEmail} 
                        className={`${styles.ouUserCard} ${isAlreadySynced ? styles.syncedUserCard : ''} ${isSelected ? styles.selectedUserCard : ''}`}
                      >
                        <div className={styles.ouUserInfo}>
                          <div className={styles.ouUserAvatar}>
                            <div className={styles.ouUserAvatarPlaceholder}>
                              {(wsUser.name?.givenName?.[0] || '').toUpperCase()}
                              {(wsUser.name?.familyName?.[0] || '').toUpperCase()}
                            </div>
                          </div>
                          
                          <div className={styles.ouUserDetails}>
                            <h6>{wsUser.name?.givenName || ''} {wsUser.name?.familyName || ''}</h6>
                            <p className={styles.ouUserEmail}>{wsUser.primaryEmail}</p>
                            <div className={styles.ouUserMeta}>
                              {isAlreadySynced ? (
                                <span className={styles.syncedBadge}>
                                  <CheckCircle size={12} />
                                  Sincronizado
                                </span>
                              ) : (
                                <span className={styles.availableBadge}>
                                  <Download size={12} />
                                  Disponible
                                </span>
                              )}
                              
                              {wsUser.suspended && (
                                <span className={styles.suspendedBadge}>
                                  Suspendido
                                </span>
                              )}
                              
                              {wsUser.isAdmin && (
                                <span className={styles.workspaceAdminBadge}>
                                  <Shield size={12} />
                                  Admin WS
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className={styles.ouUserActions}>
                          {!isAlreadySynced && (
                            <>
                              <label className={styles.ouUserCheckbox}>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleUserSelection(wsUser.primaryEmail)}
                                />
                                <span className={styles.checkboxLabel}>Seleccionar</span>
                              </label>
                              
                              <button
                                className={styles.syncSingleButton}
                                onClick={() => syncSingleUser(wsUser)}
                                disabled={isLoading}
                                title="Sincronizar este usuario"
                              >
                                <Download size={14} />
                              </button>
                            </>
                          )}
                          
                          {isAlreadySynced && (
                            <span className={styles.alreadySyncedLabel}>
                              Ya sincronizado
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Acciones para usuarios seleccionados */}
                {selectedUsers.size > 0 && (
                  <div className={styles.selectedUsersActions}>
                    <div className={styles.selectedUsersInfo}>
                      <span className={styles.selectedCount}>
                        {selectedUsers.size} usuarios seleccionados
                      </span>
                    </div>
                    
                    <div className={styles.selectedUsersButtons}>
                      <button
                        className={styles.syncSelectedButton}
                        onClick={syncSelectedUsers}
                        disabled={isLoading}
                      >
                        <Download size={16} />
                        Sincronizar seleccionados
                      </button>
                      
                      <button
                        className={styles.clearSelectionButton}
                        onClick={() => setSelectedUsers(new Set())}
                      >
                        <UserX size={16} />
                        Limpiar selección
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
};

export default WorkspaceSync;